import gsap from "gsap";
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CustomCursor from "../Cursor/CustomCursor";

/* ═══════════════════════════════════════════════════════════
   Constants — mirror Hero exactly
═══════════════════════════════════════════════════════════ */
const CELL = 48;
const BULGE_R = 180;
const BULGE_MAX = 26;
const INK_FADE = 900;
const INK_VEL_SC = 0.12;
const TRAIL_R = 88;
const TRAIL_STRK = 2.6;
const SHATTER_R = 80;
const SHATTER_DUR = 900;
const GRID_TILT = 6;

const INK = "#F5F4F0";
const SMOKE = "#0A0A08";
const SUBTLE = "#686560";

/* ═══════════════════════════════════════════════════════════
   Particle system — sample "404" letterforms
═══════════════════════════════════════════════════════════ */
function sampleLetterforms(W, H) {
  const density = Math.max(3, Math.round(W / 160));
  const off = document.createElement("canvas");
  off.width = W; off.height = H;
  const c = off.getContext("2d");
  const fs = Math.min(W * 0.30, H * 0.72, 280);
  c.fillStyle = "#fff";
  c.font = `900 ${fs}px Montreal-Bold, Arial Black, sans-serif`;
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.fillText("404", W / 2, H / 2);
  const { data } = c.getImageData(0, 0, W, H);
  const pts = [];
  for (let y = 0; y < H; y += density) {
    for (let x = 0; x < W; x += density) {
      if (data[(y * W + x) * 4 + 3] > 128) {
        pts.push({ ox: x, oy: y });
      }
    }
  }
  return pts;
}

/* ═══════════════════════════════════════════════════════════
   Magnetic letter hook — same as Hero
═══════════════════════════════════════════════════════════ */
function useMagneticLetters(ref, active) {
  useEffect(() => {
    if (!active || !ref.current) return;
    const spans = Array.from(ref.current.children);
    const MAG_R = 140;
    const MAG_ST = 0.22;
    const onMove = (e) => {
      spans.forEach(span => {
        const r = span.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = e.clientX - cx, dy = e.clientY - cy;
        const dist = Math.hypot(dx, dy);
        if (dist < MAG_R) {
          const pull = (1 - dist / MAG_R) ** 2;
          gsap.to(span, { x: dx * pull * MAG_ST, y: dy * pull * MAG_ST, duration: 0.3, ease: "power2.out" });
        } else {
          gsap.to(span, { x: 0, y: 0, duration: 0.7, ease: "elastic.out(1,0.4)" });
        }
      });
    };
    const onLeave = () => spans.forEach(s => gsap.to(s, { x: 0, y: 0, duration: 0.9, ease: "elastic.out(1,0.35)" }));
    window.addEventListener("mousemove", onMove);
    ref.current.addEventListener("mouseleave", onLeave);
    const el = ref.current;
    return () => {
      window.removeEventListener("mousemove", onMove);
      el?.removeEventListener("mouseleave", onLeave);
      spans.forEach(s => gsap.set(s, { x: 0, y: 0 }));
    };
  }, [active, ref]);
}

/* ═══════════════════════════════════════════════════════════
   Component
═══════════════════════════════════════════════════════════ */
export default function NotFound() {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const gridRef = useRef(null);   // grid canvas
  const partRef = useRef(null);   // particle canvas
  const labelRef = useRef(null);   // "404" DOM label (hidden, for measurement only)
  const subRef = useRef(null);
  const ctaRef = useRef(null);
  const metaRef = useRef(null);
  const lineRef = useRef(null);

  const mouseRef = useRef({ x: -9999, y: -9999 });
  const inkDrops = useRef([]);
  const shards = useRef([]);
  const particles = useRef([]);     // { x, y, ox, oy, vx, vy, size, alpha }
  const phaseRef = useRef("forming"); // forming | idle | shattered
  const formProg = useRef(0);
  const gridRaf = useRef(null);
  const partRaf = useRef(null);
  const glitchTO = useRef(null);

  const [ready, setReady] = useState(false);
  const isMobile = window.innerWidth <= 768;

  /* ── helper lerp ── */
  const lerp = (a, b, t) => a + (b - a) * t;

  /* ══════════════════════════════════════════════════════════
     PARTICLE CANVAS
  ══════════════════════════════════════════════════════════ */
  const initParticles = useCallback(() => {
    const canvas = partRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = wrap.offsetWidth, H = wrap.offsetHeight;
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = W + "px"; canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    /* build particles from letterform sample */
    const pts = sampleLetterforms(W, H);
    particles.current = pts.map(p => ({
      ox: p.ox, oy: p.oy,
      x: Math.random() * W,
      y: Math.random() * H + H * 0.4,
      vx: 0, vy: 0,
      size: 0.9 + Math.random() * 1.6,
      alpha: 0,
    }));
    phaseRef.current = "forming";
    formProg.current = 0;

    const REPEL_R = isMobile ? 60 : 90;
    const REPEL_FORCE = isMobile ? 3.5 : 5.8;

    const draw = () => {
      const ps = particles.current;
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const rect = canvas.getBoundingClientRect();
      const lx = mx - rect.left;
      const ly = my - rect.top;
      const ph = phaseRef.current;

      if (ph === "forming") {
        formProg.current = Math.min(formProg.current + 0.009, 1);
        const ease = 1 - Math.pow(1 - formProg.current, 3);
        let allDone = true;
        ps.forEach(p => {
          p.x = lerp(p.x, p.ox, 0.055 + ease * 0.035);
          p.y = lerp(p.y, p.oy, 0.055 + ease * 0.035);
          p.alpha = lerp(p.alpha, 1, 0.04);
          if (Math.hypot(p.x - p.ox, p.y - p.oy) > 1.2) allDone = false;
        });
        if (allDone && formProg.current > 0.99) phaseRef.current = "idle";
      } else if (ph === "idle") {
        ps.forEach(p => {
          const dx = lx - p.ox;
          const dy = ly - p.oy;
          const dist = Math.hypot(dx, dy);
          if (dist < REPEL_R && dist > 0) {
            const force = (1 - dist / REPEL_R) * REPEL_FORCE;
            p.vx -= (dx / dist) * force;
            p.vy -= (dy / dist) * force;
          }
          p.vx *= 0.80; p.vy *= 0.80;
          /* spring back to origin */
          p.vx += (p.ox - p.x) * 0.04;
          p.vy += (p.oy - p.y) * 0.04;
          p.x += p.vx; p.y += p.vy;
          p.alpha = lerp(p.alpha, 1, 0.05);
        });
      } else if (ph === "shattered") {
        ps.forEach(p => {
          p.vy += 0.22;
          p.vx *= 0.97; p.vy *= 0.97;
          p.x += p.vx; p.y += p.vy;
          p.alpha = lerp(p.alpha, 0.08, 0.018);
        });
      }

      ctx.clearRect(0, 0, W, H);
      ps.forEach(p => {
        if (p.alpha < 0.01) return;
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = INK;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      partRaf.current = requestAnimationFrame(draw);
    };
    cancelAnimationFrame(partRaf.current);
    partRaf.current = requestAnimationFrame(draw);

    const onResize = () => {
      cancelAnimationFrame(partRaf.current);
      W = wrap.offsetWidth; H = wrap.offsetHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + "px"; canvas.style.height = H + "px";
      ctx.scale(dpr, dpr);
      /* re-sample at new size */
      const pts2 = sampleLetterforms(W, H);
      particles.current = pts2.map(p => ({
        ox: p.ox, oy: p.oy,
        x: p.ox + (Math.random() - 0.5) * 40,
        y: p.oy + (Math.random() - 0.5) * 40,
        vx: 0, vy: 0,
        size: 0.9 + Math.random() * 1.6,
        alpha: 1,
      }));
      phaseRef.current = "idle";
      partRaf.current = requestAnimationFrame(draw);
    };
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(partRaf.current); window.removeEventListener("resize", onResize); };
  }, [isMobile, lerp]);

  /* ══════════════════════════════════════════════════════════
     GRID CANVAS — identical to Hero
  ══════════════════════════════════════════════════════════ */
  const initGrid = useCallback(() => {
    const canvas = gridRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || isMobile) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = wrap.offsetWidth, H = wrap.offsetHeight;
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = W + "px"; canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const onResize = () => {
      W = wrap.offsetWidth; H = wrap.offsetHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + "px"; canvas.style.height = H + "px";
      ctx.scale(dpr, dpr);
    };
    window.addEventListener("resize", onResize);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const cols = Math.ceil(W / CELL) + 1;
      const rows = Math.ceil(H / CELL) + 1;
      const { x: mx, y: my } = mouseRef.current;
      const rect = canvas.getBoundingClientRect();
      const lx = mx - rect.left, ly = my - rect.top;
      const now = Date.now();

      /* ink trail — identical to Hero */
      inkDrops.current = inkDrops.current.filter(d => now - d.born < INK_FADE);
      inkDrops.current.forEach(drop => {
        const t = (now - drop.born) / INK_FADE;
        const fade = (1 - t) ** 1.8;
        const intens = drop.intensity * fade;
        if (intens < 0.01) return;
        const { vx, vy, speed } = drop;
        const hasDir = speed > 1;
        const scan = Math.ceil(TRAIL_R / CELL) + 1;
        const cC = Math.round(drop.lx / CELL), rC = Math.round(drop.ly / CELL);
        for (let dc = -scan; dc <= scan; dc++) {
          for (let dr = -scan; dr <= scan; dr++) {
            const c = cC + dc, r = rC + dr;
            if (c < 0 || c >= cols || r < 0 || r >= rows) continue;
            let dx = c * CELL + CELL / 2 - drop.lx;
            let dy = r * CELL + CELL / 2 - drop.ly;
            if (hasDir) {
              const along = dx * vx + dy * vy;
              const px = dx - along * vx, py = dy - along * vy;
              dx = px + along * vx / TRAIL_STRK;
              dy = py + along * vy / TRAIL_STRK;
            }
            const dist = Math.hypot(dx, dy);
            if (dist >= TRAIL_R) continue;
            const norm = (1 - dist / TRAIL_R) ** 2;
            const ci = intens * norm;
            if (ci < 0.012) continue;
            const pad = 3, x = c * CELL + pad, y = r * CELL + pad, sz = CELL - pad * 2, rad = 3;
            ctx.fillStyle = `rgba(255,248,220,${(ci * 0.06).toFixed(3)})`;
            ctx.beginPath(); ctx.roundRect(x, y, sz, sz, rad); ctx.fill();
            ctx.strokeStyle = `rgba(220,200,160,${(ci * 0.55).toFixed(3)})`;
            ctx.lineWidth = norm > 0.6 ? 0.9 : 0.45;
            ctx.beginPath(); ctx.roundRect(x, y, sz, sz, rad); ctx.stroke();
            if (norm > 0.72) {
              ctx.fillStyle = `rgba(255,230,140,${((norm - 0.72) / 0.28 * intens * 0.7).toFixed(3)})`;
              ctx.beginPath(); ctx.arc(c * CELL, r * CELL, 1.5, 0, Math.PI * 2); ctx.fill();
            }
          }
        }
      });

      /* grid lines with bulge + shatter */
      ctx.strokeStyle = "rgba(245,244,240,0.07)";
      ctx.lineWidth = 0.5;
      const dp = (px, py) => {
        let rx = px, ry = py;
        const dx = px - lx, dy = py - ly;
        const d = Math.hypot(dx, dy);
        if (d < BULGE_R && d > 0) {
          const s = (1 - d / BULGE_R) ** 2;
          rx += (dx / d) * BULGE_MAX * s; ry += (dy / d) * BULGE_MAX * s;
        }
        shards.current.forEach(sh => {
          const sdx = px - sh.ox, sdy = py - sh.oy;
          const sd = Math.hypot(sdx, sdy);
          if (sd < sh.r * 2) {
            const t = Math.min((now - sh.born) / SHATTER_DUR, 1);
            const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            const frac = (1 - e) * (1 - sd / (sh.r * 2));
            rx += sh.vx * frac * 30; ry += sh.vy * frac * 30;
          }
        });
        return { rx, ry };
      };
      for (let c = 0; c < cols; c++) {
        ctx.beginPath();
        for (let r = 0; r < rows; r++) {
          const { rx, ry } = dp(c * CELL, r * CELL);
          r === 0 ? ctx.moveTo(rx, ry) : ctx.lineTo(rx, ry);
        }
        ctx.stroke();
      }
      for (let r = 0; r < rows; r++) {
        ctx.beginPath();
        for (let c = 0; c < cols; c++) {
          const { rx, ry } = dp(c * CELL, r * CELL);
          c === 0 ? ctx.moveTo(rx, ry) : ctx.lineTo(rx, ry);
        }
        ctx.stroke();
      }
      shards.current = shards.current.filter(s => now - s.born < SHATTER_DUR);
      gridRaf.current = requestAnimationFrame(draw);
    };
    gridRaf.current = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(gridRaf.current); window.removeEventListener("resize", onResize); };
  }, [isMobile]);

  /* ══════════════════════════════════════════════════════════
     MOUSE / TOUCH TRACKING
  ══════════════════════════════════════════════════════════ */
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onMove = (e) => {
      const prev = mouseRef.current;
      const dx = e.clientX - prev.x, dy = e.clientY - prev.y;
      const speed = Math.hypot(dx, dy);
      mouseRef.current = { x: e.clientX, y: e.clientY };
      const intens = Math.min(speed * INK_VEL_SC, 1);
      if (intens > 0.04 && !isMobile) {
        const rect = wrap.getBoundingClientRect();
        inkDrops.current.push({
          lx: e.clientX - rect.left, ly: e.clientY - rect.top,
          intensity: intens,
          vx: speed > 0 ? dx / speed : 0, vy: speed > 0 ? dy / speed : 0,
          speed, born: Date.now(),
        });
        if (inkDrops.current.length > 60) inkDrops.current.shift();
      }
    };
    const onTouch = (e) => {
      const t = e.touches[0];
      mouseRef.current = { x: t.clientX, y: t.clientY };
    };
    const onGridClick = (e) => {
      const rect = wrap.getBoundingClientRect();
      const lx = e.clientX - rect.left, ly = e.clientY - rect.top;
      /* grid shatter only — no particle scatter on background click */
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        shards.current.push({ ox: lx, oy: ly, vx: Math.cos(a), vy: Math.sin(a), r: SHATTER_R, born: Date.now() });
      }
    };
    const onPartClick = (e) => {
      /* only scatter if a visible particle is near the click point */
      const canvas = partRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const lx = e.clientX - rect.left, ly = e.clientY - rect.top;
      const hit = particles.current.some(p =>
        p.alpha > 0.5 && Math.hypot(p.x - lx, p.y - ly) < 18
      );
      if (!hit) return;
      if (phaseRef.current !== "idle") return;
      phaseRef.current = "shattered";
      particles.current.forEach(p => {
        const dx = p.ox - lx, dy = p.oy - ly;
        const dist = Math.hypot(dx, dy) || 1;
        const force = Math.min(700 / dist, 32);
        p.vx += (dx / dist) * force + (Math.random() - 0.5) * 5;
        p.vy += (dy / dist) * force + (Math.random() - 0.5) * 5;
      });
      /* reform after delay */
      setTimeout(() => {
        particles.current.forEach(p => {
          p.x = Math.random() * (wrapRef.current?.offsetWidth || 800);
          p.y = Math.random() * (wrapRef.current?.offsetHeight || 600) * 0.5;
          p.vx = 0; p.vy = 0;
        });
        phaseRef.current = "forming";
        formProg.current = 0;
      }, 1800);
    };
    wrap.addEventListener("mousemove", onMove);
    wrap.addEventListener("touchmove", onTouch, { passive: true });
    wrap.addEventListener("click", onGridClick);
    wrap.addEventListener("click", onPartClick);
    return () => {
      wrap.removeEventListener("mousemove", onMove);
      wrap.removeEventListener("touchmove", onTouch);
      wrap.removeEventListener("click", onGridClick);
      wrap.removeEventListener("click", onPartClick);
    };
  }, [isMobile]);

  /* ══════════════════════════════════════════════════════════
     ANIMATE IN + INIT CANVASES
  ══════════════════════════════════════════════════════════ */
  useEffect(() => {
    const tl = gsap.timeline({
      onComplete: () => {
        setReady(true);
        initGrid();
        initParticles();
      }
    });

    /* stagger meta + sub in */
    tl.fromTo(subRef.current,
      { opacity: 0, y: 28 },
      { opacity: 1, y: 0, duration: 0.9, ease: "power3.out" },
      0.2
    );
    tl.fromTo(
      [ctaRef.current, metaRef.current],
      { opacity: 0, y: 22 },
      { opacity: 1, y: 0, duration: 0.8, stagger: 0.1, ease: "power3.out" },
      0.5
    );

    return () => { tl.kill(); cancelAnimationFrame(gridRaf.current); cancelAnimationFrame(partRaf.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ══════════════════════════════════════════════════════════
     GLITCH FLICKER — periodic skew on the particle canvas
  ══════════════════════════════════════════════════════════ */
  useEffect(() => {
    const schedule = () => {
      glitchTO.current = setTimeout(() => {
        const canvas = partRef.current;
        if (!canvas) return;
        gsap.to(canvas, {
          skewX: (Math.random() - 0.5) * 10,
          x: (Math.random() - 0.5) * 12,
          duration: 0.06, ease: "none",
          onComplete: () => gsap.to(canvas, { skewX: 0, x: 0, duration: 0.14, ease: "power2.out" }),
        });
        schedule();
      }, 1800 + Math.random() * 3500);
    };
    schedule();
    return () => clearTimeout(glitchTO.current);
  }, []);

  /* ══════════════════════════════════════════════════════════
     CTA line hover (matches .lusion-link)
  ══════════════════════════════════════════════════════════ */
  const onCtaEnter = () => gsap.to(lineRef.current, { scaleX: 1, transformOrigin: "left center", duration: 0.38, ease: "power2.out" });
  const onCtaLeave = () => gsap.to(lineRef.current, { scaleX: 0, transformOrigin: "right center", duration: 0.32, ease: "power2.in" });

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <>
      <CustomCursor />

      <div
        ref={wrapRef}
        style={{
          position: "fixed", inset: 0,
          background: SMOKE,
          fontFamily: "Montreal-Mono, monospace",
          cursor: "none",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "clamp(24px,5vw,60px) clamp(20px,8vw,80px)",
        }}
      >
        {/* ── grid canvas (behind everything) ── */}
        <canvas
          ref={gridRef}
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0,
            pointerEvents: "none", zIndex: 0,
            transform: `perspective(900px) rotateX(${GRID_TILT}deg)`,
            transformOrigin: "center 60%",
          }}
        />

        {/* ── particle canvas ("404" letterforms) ── */}
        <canvas
          ref={partRef}
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />

        {/* ── top bar ── */}
        <div style={{ position: "relative", zIndex: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontFamily: "Montreal-Mono,monospace", fontSize: "clamp(9px,1.1vw,11px)", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(245,244,240,0.32)" }}>
            Error / Not Found
          </span>
          <span style={{ fontFamily: "Montreal-Mono,monospace", fontSize: "clamp(9px,1.1vw,11px)", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(245,244,240,0.32)" }}>
            Page corrupted
          </span>
        </div>

        {/* ── descriptor (right-aligned like Hero) ── */}
        <div style={{ position: "relative", zIndex: 10, flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", paddingBottom: "clamp(16px,3vw,36px)" }}>
          <p
            ref={subRef}
            style={{
              marginLeft: "auto",
              maxWidth: 400,
              fontFamily: "Montreal-Regular,'Plus Jakarta Sans',sans-serif",
              fontSize: "clamp(13px,1.25vw,15px)",
              lineHeight: 1.75,
              color: SUBTLE,
              opacity: 0,
            }}
          >
            You followed a broken link.<br />
            This page no longer exists or never did.<br />
          </p>
        </div>

        {/* ── bottom bar ── */}
        <div
          ref={metaRef}
          style={{ position: "relative", zIndex: 10, display: "flex", alignItems: "flex-end", justifyContent: "space-between", opacity: 0 }}
        >
          {/* CTA */}
          <div>
            <button
              ref={ctaRef}
              onMouseEnter={onCtaEnter}
              onMouseLeave={onCtaLeave}
              onClick={() => navigate("/")}
              style={{
                position: "relative",
                background: "transparent", border: "none", padding: 0,
                cursor: "none",
                fontFamily: "Montreal-Bold,'Plus Jakarta Sans',sans-serif",
                fontWeight: 900,
                fontSize: "clamp(1.2rem,3.8vw,3.8vw)",
                letterSpacing: "-0.025em",
                lineHeight: 1,
                color: INK,
                textTransform: "uppercase",
              }}
            >
              Return Home
              <span
                ref={lineRef}
                style={{
                  position: "absolute", left: 0, bottom: -3,
                  width: "100%", height: 2,
                  background: INK,
                  transform: "scaleX(0)",
                  transformOrigin: "right center",
                  display: "block",
                }}
              />
            </button>

          </div>

        </div>
      </div>
    </>
  );
}
